import { Sentence } from "types";
import { RootStore } from "stores/rootStore";
import {BuildSegments} from "components/EditableTextarea/segments";
import {renderToString} from "react-dom/server";
import React from "react";
import {makeAutoObservable} from "mobx";
import {translate} from "i18n/translator";
import {escapeHtml} from "components/EditableTextarea/selection";

/** Where a partial scan stopped: the first `at` characters went to the scan, the rest did not.
 *  `kind`/`limit` name the plan limit that cut it — the per-scan character cap or the words
 *  the allowance still buys, whichever is shorter. */
export type ScanCut = {at: number, kind: 'chars' | 'words', limit: number};

/** A cut as an editor shows it: over the whole text, and whether a run of the full text is
 *  already on its way (the divider then says so instead of selling it). */
export type EditorCut = ScanCut & {text: string, scanning: boolean};

const attr = (value: string) => escapeHtml(value).replaceAll('"', '&quot;');

/**
 * A divider and the greyed text after it. The divider carries its label in an attribute, painted
 * by CSS: the editor reads its text back from the DOM (htmlVisibleText), and a text node there
 * would end up in the user's text. `between` is raw markup put after the divider.
 */
const dividerHtml = (label: string, modifier: string, between: string, rest: string) =>
    `<span class="scan-limit-divider${modifier ? ` ${modifier}` : ''}" contenteditable="false" data-label="${attr(label)}"></span>`
    + between + `<span class="scan-unscanned">${escapeHtml(rest)}</span>`;

/** The sentences over text[0, at): the one `at` falls in is cut there, its chunks with it. */
const sentencesBefore = (sentences: Sentence[], at: number): Sentence[] => {
    const out: Sentence[] = [];
    let start = 0;
    for (const s of sentences) {
        const text = s.text ?? '';
        if (start >= at) break;
        if (start + text.length <= at) {
            out.push(s);
        } else {
            const keep = at - start;
            let left = keep;
            const chunks = s.chunks?.flatMap(c => {
                const part = c.text.slice(0, Math.max(0, left));
                left -= c.text.length;
                return part ? [{...c, text: part}] : [];
            });
            out.push({...s, text: text.slice(0, keep), chunks});
        }
        start += text.length;
    }
    return out;
};

/** The login divider closes the rest of the text: once the user has deleted all of it, the divider goes too. */
const withoutEmptyPreviewRest = (html: string): string => {
    if (!html.includes('scan-limit-divider--login')) return html;
    const root = document.createElement('div');
    root.innerHTML = html;
    const divider = root.querySelector('.scan-limit-divider--login');
    if (!divider) return html;
    const rest = document.createRange();
    rest.setStartAfter(divider);
    rest.setEnd(root, root.childNodes.length);
    if (rest.toString().trim()) return html;
    rest.setStartBefore(divider);
    rest.deleteContents();
    return root.innerHTML;
};

/**
 * EditorStore
 *
 * Purpose
 * - Holds source sentences and produces rich HTML segments used by the editor.
 * - Tracks the latest built markup (`buildData`) along with a simple history to
 *   support undo (Ctrl/Cmd+Z) during inline editing.
 * - Provides switches to control whether we show word-level or sentence-level
 *   highlights, and regenerates derived segments accordingly.
 */
export class EditorStore {

    html: string = "";
    segments: Sentence[] = [];
    isLoading: boolean = false;

    root: RootStore;
    selectedSentences: string[] = [];

    isFrozen: boolean = false;

    /** A partial scan's unscanned rest, rendered after the scanned markup (see withCut). */
    cut: EditorCut | null = null;

    /** The text offset the markup's colours end at, the login divider there; null colours it all. */
    preview: number | null = null;

    constructor(root: RootStore) {
        this.root = root;
        makeAutoObservable(this);
    }

    setFlozen(isFrozen: boolean) {
        this.isFrozen = isFrozen;
    }

    get haveSegments() {
        return !!this.segments?.length;
    }

    get isFilled() {
        return !!this.segments?.length || !!this.html;
    }

    public setSegments(segments: Sentence[], {preview = null}: {preview?: number | null} = {}) {
        // The full text's result has landed: its segments already hold what was the rest.
        if (this.cut?.scanning) this.cut = null;
        this.preview = preview;
        let html = renderToString(this.generateNode(preview === null ? segments : sentencesBefore(segments, preview)) as any);
        if (preview !== null) html += this.previewDivider(segments.map(s => s.text ?? '').join('').slice(preview));
        this.setHtml(this.withCut(html));
        this.segments = segments;
    }

    /** `cut` splits the text: the part before it is the scan's, the rest is shown as unscanned —
     *  or, with `scanning`, as on its way: the full text's run started over a partial result. */
    setText(text: string, cut: ScanCut | null = null, {scanning = false}: {scanning?: boolean} = {}) {
        this.segments = [];
        this.preview = null;
        this.cut = cut ? {...cut, text, scanning} : null;
        // Escape HTML-sensitive chars; let <pre> render newlines/spaces
        this.setHtml(this.withCut(escapeHtml(cut ? text.slice(0, cut.at) : text)));
    }

    /** The full text's run landed with nothing to paint, or failed: the divider goes, the text stays. */
    dropScanningCut() {
        if (this.cut?.scanning) this.dropCut();
    }

    /** No result stands on the cut any more (its run failed): the text goes back to plain. */
    dropCut() {
        if (this.cut) this.setText(this.cut.text);
    }

    /** An anon's result is cut at their preview (what is painted: InputStore.resultHighlightOff). */
    get previewShown(): boolean {
        return this.preview !== null;
    }

    /** The cut the result on screen was made with — none while the full text is being scanned. */
    get settledCut(): EditorCut | null {
        return this.cut?.scanning ? null : this.cut;
    }

    /** The login divider after an anon's preview, then the rest of the text. The paragraph break
     *  the rest opens with stays in the text but is hidden: the divider already stands on a line
     *  of its own. */
    private previewDivider(rest: string): string {
        const [, gap, body] = rest.match(/^(\s*)([\s\S]*)$/)!;
        return dividerHtml(translate('scan-preview-divider'), 'scan-limit-divider--login',
            (gap ? `<span class="scan-limit-divider__gap">${gap}</span>` : ''), body);
    }

    /** The scanned markup followed by the divider and the unscanned rest. */
    private withCut(html: string): string {
        const cut = this.cut;
        if (!cut) return html;
        const label = translate(cut.scanning ? 'scan-limit-divider-scanning' : 'scan-limit-divider');
        return html + dividerHtml(label, cut.scanning ? 'scan-limit-divider--scanning' : '', '', cut.text.slice(cut.at));
    }

    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    private generateNode(sentences: Sentence[] ) {
        return React.createElement(BuildSegments, {
            sentences: sentences,
            selectedSentence: this.selectedSentences,
            highlightSentences: this.root.input.isHighlightSentences
        });
    }


    public onEdited(newHtml: string) {
        this.setHtml(this.preview === null ? newHtml : withoutEmptyPreviewRest(newHtml));
    }

    public setHtml(newHtml: string) {
        if (newHtml === "<br>") {
            newHtml = ""
        }
        this.html = newHtml;
    }

    public clear() {
        this.html = "";
        this.cut = null;
        this.preview = null;
        this.segments = [];
        this.selectedSentences = [];
    }
}